/**
 * Aplica o desconto do contador sobre o preco, replicando EXATAMENTE a formula do
 * backend (aplicarDesconto): desconto arredondado a 2 casas HALF_UP; final com piso 0.
 *
 * NAO usar arredondarParaMoeda(): aquele arredonda para a unidade da moeda (0 casas em
 * PYG) e divergiria do checkout em casos de fracao. Aqui o DESCONTO e arredondado a 2 casas.
 *
 * @param preco      preco base do plano (plano.valor)
 * @param percentual percentual do contrato ATIVO (ex.: 20 = 20%)
 * @returns preco final com desconto (>= 0); devolve o proprio preco se entradas invalidas.
 */
export function aplicarDescontoContador(preco: number, percentual: number): number {
  if (!preco || preco <= 0 || !percentual || percentual <= 0) return preco ?? 0;
  const desconto = arredondar2CasasHalfUp((preco * percentual) / 100);
  const final = preco - desconto;
  return final > 0 ? final : 0;
}

/**
 * Arredondamento a 2 casas HALF_UP, sem depender de epsilon fixo.
 *
 * Estrategia: em vez de somar uma constante de correcao de ponto flutuante
 * (que falha em magnitudes maiores, pois o ULP do double cresce com o valor —
 * ex.: 9.995 vira 9.994999999999999218... e Number.EPSILON e pequeno demais
 * para corrigir o erro nessa faixa), usa-se a representacao decimal "curta"
 * do double via `toString()` (o mesmo algoritmo de shortest round-trip usado
 * por `Double.toString()` no Java, base do `BigDecimal.valueOf(double)`).
 * Essa string decimal e entao arredondada HALF_UP por manipulacao de digitos
 * (sem aritmetica de ponto flutuante), replicando exatamente o backend.
 */
function arredondar2CasasHalfUp(valor: number): number {
  if (!isFinite(valor)) return valor;
  const negativo = valor < 0;
  const abs = Math.abs(valor);
  const str = paraNotacaoDecimal(abs);

  const pontoIndex = str.indexOf('.');
  const parteInteira = pontoIndex === -1 ? str : str.slice(0, pontoIndex);
  const parteDecimal = (pontoIndex === -1 ? '' : str.slice(pontoIndex + 1)).padEnd(3, '0');

  const doisPrimeiros = parteDecimal.slice(0, 2);
  const digitoDesempate = parteDecimal.charCodeAt(2) - 48; // 3a casa decide HALF_UP

  let centavos = parseInt(parteInteira, 10) * 100 + parseInt(doisPrimeiros, 10);
  if (digitoDesempate >= 5) centavos += 1;

  const resultado = centavos / 100;
  return negativo ? -resultado : resultado;
}

/**
 * Garante notacao decimal plana (sem "e"/"E"), convertendo notacao cientifica
 * por deslocamento de digitos — sem introduzir arredondamento de ponto flutuante.
 */
function paraNotacaoDecimal(valor: number): string {
  const str = valor.toString();
  if (!/e/i.test(str)) return str;

  const match = str.match(/^(\d+)(?:\.(\d+))?e([+-]\d+)$/i);
  if (!match) return str;

  const [, intPart, fracPart = '', expStr] = match;
  const exp = parseInt(expStr, 10);
  const digitos = intPart + fracPart;
  const novoPonto = intPart.length + exp;

  if (novoPonto <= 0) return '0.' + '0'.repeat(-novoPonto) + digitos;
  if (novoPonto >= digitos.length) return digitos + '0'.repeat(novoPonto - digitos.length);
  return digitos.slice(0, novoPonto) + '.' + digitos.slice(novoPonto);
}
